import LoadingButton from '@mui/lab/LoadingButton';
import {
    alpha,
    Box,
    Button,
    Checkbox,
    DialogContent,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import moment from 'moment';
import { useDialog } from 'muibox';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { OnlyFansMassDMMessageScheduleDocumentWithColor } from '../../../../hooks/useMessageUsersMassDmList';
import DialogAdaptive from '../../../common/DialogAdaptive';
import StyledCardTitleBar from '../../../common/StyledCardTitleBar';
import StyledDialogActions from '../../../common/StyledDialogActions';

type BulkMode = 'pause' | 'resume';

type Props = {
    fetchActiveScheduledMessages: () => Promise<OnlyFansMassDMMessageScheduleDocumentWithColor[]>;
    refetchMassDmsSchedule: () => void;
    bulkSetMassDmScheduleInactiveStatus: (massDmIds: string[], inactive: boolean) => Promise<{ success: boolean }>;
    bulkSetMassDmScheduleInactiveStatusLoading: boolean;
    open: boolean;
    setOpen: (open: boolean) => void;
};

type SortField = 'name' | 'scheduleDate' | 'price';
type SortDirection = 'asc' | 'desc';

const MessageUsersAutoMassDmBulkPauseDialog = (props: Props) => {
    const {
        fetchActiveScheduledMessages,
        refetchMassDmsSchedule,
        bulkSetMassDmScheduleInactiveStatus,
        bulkSetMassDmScheduleInactiveStatusLoading,
        open,
        setOpen,
    } = props;
    const { enqueueSnackbar } = useSnackbar();
    const dialog = useDialog();

    const [mode, setMode] = useState<BulkMode>('pause');
    const [scheduledMessages, setScheduledMessages] = useState<OnlyFansMassDMMessageScheduleDocumentWithColor[]>([]);
    const [loadingScheduledMessages, setLoadingScheduledMessages] = useState(false);
    const [selectedScheduleIds, setSelectedScheduleIds] = useState<string[]>([]);
    const [selectedScheduledMessages, setSelectedScheduledMessages] = useState<OnlyFansMassDMMessageScheduleDocumentWithColor[]>([]);
    const [sortField, setSortField] = useState<SortField>('name');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

    const isPauseMode = mode === 'pause';
    const accentColor = isPauseMode ? 'warning' : 'success';

    useEffect(() => {
        if (!open) {
            return;
        }

        setLoadingScheduledMessages(true);

        fetchActiveScheduledMessages()
            .then(messages => {
                setScheduledMessages(messages);
            })
            .finally(() => {
                setLoadingScheduledMessages(false);
            });
    }, [open, fetchActiveScheduledMessages]);

    useEffect(() => {
        setSelectedScheduleIds([]);
        setSelectedScheduledMessages([]);
    }, [mode]);

    const eligibleScheduledMessages = useMemo(
        () =>
            scheduledMessages.filter(scheduledMessage =>
                isPauseMode ? scheduledMessage.active && !scheduledMessage.inactive : scheduledMessage.active && scheduledMessage.inactive,
            ),
        [scheduledMessages, isPauseMode],
    );

    const sortedScheduledMessages = useMemo(() => {
        return [...eligibleScheduledMessages].sort((a, b) => {
            let aValue: string | number;
            let bValue: string | number;

            switch (sortField) {
                case 'name':
                    aValue = a.name.toLowerCase();
                    bValue = b.name.toLowerCase();
                    break;
                case 'scheduleDate':
                    aValue = a.scheduleDate ? new Date(a.scheduleDate).getTime() : 0;
                    bValue = b.scheduleDate ? new Date(b.scheduleDate).getTime() : 0;
                    break;
                case 'price':
                    aValue = a.price ?? 0;
                    bValue = b.price ?? 0;
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) {
                return sortDirection === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortDirection === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [eligibleScheduledMessages, sortField, sortDirection]);

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const handleClose = () => {
        setMode('pause');
        setScheduledMessages([]);
        setSelectedScheduledMessages([]);
        setSelectedScheduleIds([]);
        setOpen(false);
    };

    const handleSubmit = () => {
        const actionLabel = isPauseMode ? 'pause' : 'resume';

        dialog
            .confirm({
                title: isPauseMode ? 'Pause Scheduled Mass DMs' : 'Resume Scheduled Mass DMs',
                message: isPauseMode
                    ? `Are you sure you want to pause ${selectedScheduledMessages.length.toLocaleString()} Scheduled Mass DMs? They will stay in your active list and skip sending until resumed.`
                    : `Are you sure you want to resume ${selectedScheduledMessages.length.toLocaleString()} Scheduled Mass DMs? They will return to their normal schedule.`,
                ok: {
                    text: isPauseMode ? 'Pause' : 'Resume',
                    color: accentColor,
                    variant: 'contained',
                },
                cancel: {
                    text: 'Cancel',
                    color: 'secondary',
                    variant: 'text',
                },
            })
            .then(() => {
                bulkSetMassDmScheduleInactiveStatus(selectedScheduleIds, isPauseMode)
                    .then(res => {
                        if (res.success) {
                            enqueueSnackbar(`Scheduled Mass DMs ${actionLabel}d successfully`, { variant: 'success' });
                            refetchMassDmsSchedule();
                            handleClose();
                        } else {
                            enqueueSnackbar(`Failed to ${actionLabel} Scheduled Mass DMs`, { variant: 'error' });
                        }
                    })
                    .catch(err => {
                        console.error(err);
                        enqueueSnackbar(`Failed to ${actionLabel} Scheduled Mass DMs`, { variant: 'error' });
                    });
            })
            .catch(() => {});
    };

    return (
        <DialogAdaptive open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
            <StyledCardTitleBar title="Scheduled Mass DMs Bulk Pause / Resume" />
            <DialogContent sx={{ pl: 0, pr: 0 }}>
                <Stack direction="column" spacing={2}>
                    <Stack direction="row" spacing={1} alignItems="center" pl={2} pr={2}>
                        <ToggleButtonGroup
                            color={accentColor}
                            value={mode}
                            exclusive
                            size="small"
                            onChange={(_event, value: BulkMode | null) => {
                                if (value !== null) {
                                    setMode(value);
                                }
                            }}
                        >
                            <ToggleButton value="pause">Pause</ToggleButton>
                            <ToggleButton value="resume">Resume</ToggleButton>
                        </ToggleButtonGroup>
                    </Stack>

                    <Typography variant="body1" color="text.secondary" pl={2} pr={2}>
                        {isPauseMode
                            ? 'Select the active Scheduled Mass DMs you want to pause. Paused schedules remain in your active list and skip sending until resumed.'
                            : 'Select the paused Scheduled Mass DMs you want to resume. They will return to their normal schedule.'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" pl={2} pr={2}>
                        {selectedScheduledMessages.length.toLocaleString()} Scheduled Mass DMs selected
                    </Typography>

                    {loadingScheduledMessages ? (
                        <Typography variant="body2" color="text.secondary" pl={2} pr={2}>
                            Loading active Scheduled Mass DMs...
                        </Typography>
                    ) : sortedScheduledMessages.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" pl={2} pr={2}>
                            {isPauseMode
                                ? 'No active Scheduled Mass DMs available to pause.'
                                : 'No paused Scheduled Mass DMs available to resume.'}
                        </Typography>
                    ) : (
                        <Box
                            sx={{
                                width: '100%',
                                overflow: 'hidden',
                                backgroundColor: theme => alpha(theme.palette[accentColor].main, 0.1),
                            }}
                        >
                            <TableContainer
                                sx={{
                                    maxHeight: 400,
                                    '& .MuiTableCell-root': {
                                        padding: '4px 8px',
                                    },
                                    '& .MuiTableCell-root:first-of-type': {
                                        paddingLeft: 0,
                                    },
                                    '& .MuiTableCell-root:last-of-type': {
                                        paddingRight: 2,
                                    },
                                }}
                            >
                                <Table
                                    size="small"
                                    sx={{
                                        '& thead th': {
                                            position: 'sticky',
                                            top: 0,
                                            backgroundColor: isPauseMode ? 'rgb(239, 228, 198)' : 'rgb(214, 235, 214)',
                                            zIndex: 1,
                                        },
                                        '& th:first-of-type, & td:first-of-type': {
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 1,
                                        },
                                    }}
                                >
                                    <TableHead
                                        sx={{
                                            backgroundColor: isPauseMode ? 'rgb(239, 228, 198)' : 'rgb(214, 235, 214)',
                                            '& th:first-of-type': {
                                                position: 'sticky',
                                                left: 0,
                                                zIndex: 2,
                                            },
                                        }}
                                    >
                                        <TableRow
                                            sx={{
                                                '& th': {
                                                    backgroundColor: theme => theme.palette.background.paper,
                                                    color: theme => theme.palette.text.primary,
                                                    fontWeight: 'bold',
                                                },
                                            }}
                                        >
                                            <TableCell width={50} align="center">
                                                <Checkbox
                                                    indeterminate={
                                                        selectedScheduleIds.length > 0 &&
                                                        selectedScheduleIds.length < sortedScheduledMessages.length
                                                    }
                                                    checked={
                                                        sortedScheduledMessages.length > 0 &&
                                                        selectedScheduledMessages.length === sortedScheduledMessages.length
                                                    }
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        if (e.target.checked) {
                                                            setSelectedScheduleIds(
                                                                sortedScheduledMessages.map(scheduledMessage => scheduledMessage._id),
                                                            );
                                                            setSelectedScheduledMessages(sortedScheduledMessages);
                                                        } else {
                                                            setSelectedScheduleIds([]);
                                                            setSelectedScheduledMessages([]);
                                                        }
                                                    }}
                                                    color={accentColor}
                                                    size="small"
                                                    sx={{
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            backgroundColor: 'transparent',
                                                        },
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={sortField === 'name'}
                                                    direction={sortField === 'name' ? sortDirection : 'asc'}
                                                    onClick={() => handleSort('name')}
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        '& .MuiTableSortLabel-icon': {
                                                            color: 'inherit !important',
                                                        },
                                                    }}
                                                >
                                                    Mass DM
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={sortField === 'scheduleDate'}
                                                    direction={sortField === 'scheduleDate' ? sortDirection : 'asc'}
                                                    onClick={() => handleSort('scheduleDate')}
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        '& .MuiTableSortLabel-icon': {
                                                            color: 'inherit !important',
                                                        },
                                                    }}
                                                >
                                                    Next Send
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell align="center" width={80}>
                                                <TableSortLabel
                                                    active={sortField === 'price'}
                                                    direction={sortField === 'price' ? sortDirection : 'asc'}
                                                    onClick={() => handleSort('price')}
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        '& .MuiTableSortLabel-icon': {
                                                            color: 'inherit !important',
                                                        },
                                                    }}
                                                >
                                                    Price
                                                </TableSortLabel>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {sortedScheduledMessages.map(scheduledMessage => (
                                            <TableRow
                                                key={scheduledMessage._id}
                                                sx={{
                                                    backgroundColor: theme =>
                                                        selectedScheduleIds.includes(scheduledMessage._id)
                                                            ? alpha(theme.palette[accentColor].main, 0.2)
                                                            : 'transparent',
                                                }}
                                            >
                                                <TableCell align="center">
                                                    <Checkbox
                                                        checked={selectedScheduleIds.includes(scheduledMessage._id)}
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                            if (e.target.checked) {
                                                                setSelectedScheduleIds([...selectedScheduleIds, scheduledMessage._id]);
                                                                setSelectedScheduledMessages([
                                                                    ...selectedScheduledMessages,
                                                                    scheduledMessage,
                                                                ]);
                                                            } else {
                                                                setSelectedScheduleIds(
                                                                    selectedScheduleIds.filter(id => id !== scheduledMessage._id),
                                                                );
                                                                setSelectedScheduledMessages(
                                                                    selectedScheduledMessages.filter(sm => sm._id !== scheduledMessage._id),
                                                                );
                                                            }
                                                        }}
                                                        color={accentColor}
                                                        size="small"
                                                        sx={{
                                                            cursor: 'pointer',
                                                            '&:hover': {
                                                                backgroundColor: 'transparent',
                                                            },
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="text.primary" noWrap>
                                                        {scheduledMessage.name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="text.secondary" noWrap>
                                                        {scheduledMessage.scheduleDate
                                                            ? moment(scheduledMessage.scheduleDate).format('DD/MM/YYYY hh:mm A')
                                                            : 'Not scheduled'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography variant="body2" color="text.secondary" noWrap>
                                                        {(scheduledMessage.type || 'manual') === 'template'
                                                            ? 'From template'
                                                            : scheduledMessage.price
                                                            ? `$${scheduledMessage.price.toFixed(2)}`
                                                            : 'Free'}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </Stack>
            </DialogContent>

            <StyledDialogActions>
                <Button onClick={handleClose} color="secondary" variant="text">
                    Cancel
                </Button>
                <LoadingButton
                    variant="contained"
                    color={accentColor}
                    size="small"
                    loading={bulkSetMassDmScheduleInactiveStatusLoading}
                    disabled={selectedScheduleIds.length === 0}
                    onClick={() => {
                        handleSubmit();
                    }}
                >
                    <span>{isPauseMode ? 'Pause Selected' : 'Resume Selected'}</span>
                </LoadingButton>
            </StyledDialogActions>
        </DialogAdaptive>
    );
};

export default MessageUsersAutoMassDmBulkPauseDialog;
